/* Public team page — every member's QR code for a batch (team) registration.
   Reached right after team registration and via the emailed team link. The
   group_id in the URL is an unguessable UUID acting as the access token.
   Screenshot-friendly by design: captains share this in the group chat. */
import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { Users, Loader2, Copy, ExternalLink, Clock, CheckCircle, Share2, XCircle } from 'lucide-react';
import { fmtDate } from '../utils/dates';

const API = process.env.REACT_APP_BACKEND_URL;
const RED = '#E63946';

const STATUS_COLORS = {
  registered: '#22C55E', active: '#22C55E', playing: '#3B82F6',
  waitlisted: '#8B5CF6', paused: '#F59E0B', done: '#737373', flagged: '#EF4444',
};

export default function SportsOpenPlayTeamPage() {
  const { slug, sid, groupId } = useParams();
  const [params] = useSearchParams();
  const returnPay = params.get('pay'); // 'success' | 'cancel' | null (PayMongo return)
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');
  const [payState, setPayState] = useState(
    returnPay === 'success' ? 'checking' : returnPay === 'cancel' ? 'cancelled' : null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/sports/public/${slug}/open-play/${sid}/team/${groupId}`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Team not found');
    }
  }, [slug, sid, groupId]);

  useEffect(() => { load(); }, [load]);

  // PayMongo return: poll the team's shared payment until confirmed.
  useEffect(() => {
    if (returnPay !== 'success') return;
    let stop = false; let tries = 0;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/api/sports/public/${slug}/open-play/${sid}/team/${groupId}/pay/status`);
        if (r.data?.paid) { if (!stop) { setPayState('paid'); load(); } return; }
      } catch { /* retry */ }
      tries += 1;
      if (tries >= 8) { if (!stop) setPayState('pending'); return; }
      setTimeout(poll, 2500);
    };
    poll();
    return () => { stop = true; };
  }, [returnPay, slug, sid, groupId, load]);

  const origin = window.location.origin;
  const playerUrl = (token) => `${origin}/sports/open-play/player/${token}`;
  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 1500);
  };

  const page = { minHeight: '100vh', background: '#0A0A0A', color: '#fff', fontFamily: 'Manrope, sans-serif', padding: '32px 16px' };
  const card = { maxWidth: 560, margin: '0 auto', background: '#141414', border: '1px solid #262626', borderRadius: 16, padding: 24 };

  if (!data) {
    return (
      <div style={page}>
        <div style={{ ...card, textAlign: 'center' }}>
          {error ? <p style={{ color: '#EF4444' }}>{error}</p>
            : <Loader2 className="h-8 w-8 mx-auto animate-spin" style={{ color: RED }} />}
        </div>
      </div>
    );
  }

  const s = data.session;
  const fee = parseFloat(s.entry_fee || 0);
  const allWaitlisted = data.players.length > 0 && data.players.every(p => p.status === 'waitlisted');
  // What the team still owes, as the desk counts it (comps and payments
  // subtracted, waitlisted members excluded) — not fee × members (R1-114).
  const owed = data.players.reduce((n, p) => n + Number(p.balance ?? 0), 0);
  const hasBalances = data.players.some(p => p.balance != null);
  const unpaid = fee > 0 && (hasBalances ? owed > 0.005
    : data.players.some(p => !['paid', 'free'].includes(p.payment_status)));

  return (
    <div style={page} data-testid="op-team-page">
      <div style={card}>
        <div className="flex items-center gap-2 mb-1">
          <Users className="h-5 w-5" style={{ color: RED }} />
          <p className="text-xs font-bold uppercase tracking-widest" style={{ color: RED }}>Team — Open Play</p>
        </div>
        <h1 className="text-2xl font-black uppercase mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>{s.name}</h1>
        <p className="text-xs mb-1" style={{ color: '#737373' }}>
          {fmtDate(s.date)} · {s.time_start}–{s.time_end} · {data.facility?.name}
        </p>
        {data.contact_name && (
          <p className="text-xs mb-3" style={{ color: '#525252' }}>Registered by {data.contact_name}</p>
        )}

        {allWaitlisted ? (
          <div className="flex items-start gap-2 p-3 rounded-lg mb-4" style={{ background: 'rgba(139,92,246,0.1)' }}>
            <Clock className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: '#8B5CF6' }} />
            <p className="text-xs" style={{ color: '#8B5CF6' }}>
              The session was full, so your whole team is on the waitlist together. You&apos;ll be promoted as spots open.
            </p>
          </div>
        ) : (
          <div className="flex items-start gap-2 p-3 rounded-lg mb-4" style={{ background: 'rgba(34,197,94,0.08)' }}>
            <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: '#22C55E' }} />
            <p className="text-xs" style={{ color: '#A3A3A3' }}>
              Everyone below is in. Each player checks in on game day with their own QR code —
              screenshot this page or share the link in your group chat.
            </p>
          </div>
        )}
        {/* PayMongo return banner */}
        {payState === 'checking' && (
          <div className="flex items-center gap-2 p-3 rounded-lg mb-4" style={{ background: '#1A1A1A' }}>
            <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" style={{ color: RED }} />
            <p className="text-xs" style={{ color: '#A3A3A3' }}>Confirming your team&apos;s payment…</p>
          </div>
        )}
        {payState === 'paid' && (
          <div className="flex items-start gap-2 p-3 rounded-lg mb-4" style={{ background: 'rgba(34,197,94,0.1)' }}>
            <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: '#22C55E' }} />
            <p className="text-xs" style={{ color: '#22C55E' }}>
              Payment confirmed — the whole team&apos;s entry fees are settled.
            </p>
          </div>
        )}
        {payState === 'pending' && (
          <div className="flex items-start gap-2 p-3 rounded-lg mb-4" style={{ background: 'rgba(245,158,11,0.1)' }}>
            <Clock className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: '#F59E0B' }} />
            <p className="text-xs" style={{ color: '#F59E0B' }}>
              Payment is still processing — your team is registered, and the organizer will see it confirmed shortly.
            </p>
          </div>
        )}
        {payState === 'cancelled' && (
          <div className="flex items-start gap-2 p-3 rounded-lg mb-4" style={{ background: 'rgba(239,68,68,0.1)' }}>
            <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: '#EF4444' }} />
            <p className="text-xs" style={{ color: '#A3A3A3' }}>
              Payment cancelled. Your team is still registered — you can pay at the venue instead.
            </p>
          </div>
        )}

        {unpaid && payState !== 'paid' && (
          <p className="text-xs p-2 rounded mb-4" style={{ background: '#1A1A1A', color: '#F59E0B' }}>
            {hasBalances
              ? <>Still to pay: ₱{owed.toLocaleString()} for the team.</>
              : <>Entry fees: ₱{fee.toLocaleString()} × {data.players.length} = ₱{(fee * data.players.length).toLocaleString()} total.</>}
          </p>
        )}

        <button data-testid="op-team-copy-link"
          onClick={() => copy(window.location.href, 'team')}
          className="w-full mb-4 py-2.5 rounded-lg text-xs font-bold uppercase inline-flex items-center justify-center gap-2"
          style={{ background: '#262626', color: '#fff', border: '1px solid #333' }}>
          <Share2 className="h-4 w-4" /> {copied === 'team' ? 'Link copied!' : 'Copy team link'}
        </button>

        <div className="space-y-3">
          {data.players.map((p, i) => (
            <div key={p.id} data-testid={`op-team-member-${i}`}
              className="flex items-center gap-3 p-3 rounded-xl"
              style={{ background: '#0F0F0F', border: '1px solid #262626' }}>
              <div style={{ background: '#fff', borderRadius: 10, padding: 6, flexShrink: 0 }}>
                <QRCodeSVG value={playerUrl(p.personal_token)} size={96} level="H"
                  bgColor="#FFFFFF" fgColor="#0A0A0A" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold truncate">{p.name}</p>
                {p.division_name && <p className="text-[11px]" style={{ color: '#737373' }}>{p.division_name}</p>}
                <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase"
                  style={{ background: `${STATUS_COLORS[p.status] || '#737373'}22`, color: STATUS_COLORS[p.status] || '#737373' }}>
                  {p.status}
                </span>
                {['paid', 'free'].includes(p.payment_status) && (
                  <span className="inline-block mt-1 ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase"
                    style={{ background: 'rgba(34,197,94,0.13)', color: '#22C55E' }}>
                    {p.payment_status === 'free' ? 'Free' : 'Paid'}
                  </span>
                )}
                <div className="flex gap-2 mt-2">
                  <a href={playerUrl(p.personal_token)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-bold"
                    style={{ background: RED, color: '#fff', textDecoration: 'none' }}>
                    <ExternalLink className="h-3 w-3" /> Player page
                  </a>
                  <button onClick={() => copy(playerUrl(p.personal_token), p.id)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-bold"
                    style={{ background: '#262626', color: '#A3A3A3', border: '1px solid #333' }}>
                    <Copy className="h-3 w-3" /> {copied === p.id ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <a href={`/book/${slug}/open-play/${sid}`} className="block text-center text-xs mt-5"
          style={{ color: '#525252', textDecoration: 'none' }}>
          View live session →
        </a>
      </div>
    </div>
  );
}
